import React, { useState, useEffect } from 'react';
import { ExternalLink, Code2, Smartphone, Monitor, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';

const ShowcaseSection = () => {
  const [projects, setProjects] = useState([]);
  const [hoveredApp, setHoveredApp] = useState(null);
  const [loading, setLoading] = useState(true);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [backendUrl]);

  if (loading) {
    return (
      <div className="py-20 bg-black flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-silver border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (projects.length === 0) return null;

  return (
    <div id="about">
      <section id="showcase" className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-silver/60 text-sm font-mono tracking-widest uppercase mb-4">
            Our Work
          </p>
          <h2 className="text-4xl md:text-6xl font-bold text-silver mb-6">
            Application
            <span className="block gradient-text">Showcase</span>
          </h2>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Explore our portfolio of enterprise applications, each crafted with precision
            and designed to solve complex business challenges.
          </p>
        </div>

        {/* Showcase Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {projects.map((project, index) => (
            <div
              key={project.id}
              className="group relative bg-gradient-to-br from-gray-900 to-black rounded-xl overflow-hidden border border-gray-700 hover:border-gray-600 transition-all duration-500 card-hover-effect"
              onMouseEnter={() => setHoveredApp(index)}
              onMouseLeave={() => setHoveredApp(null)}
            >
              {/* project Preview Area */}
              <div className="relative h-64 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center overflow-hidden">
                {project.image ? (
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                ) : (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="absolute inset-0 grid grid-cols-6 grid-rows-4 opacity-10">
                      {Array.from({ length: 24 }).map((_, i) => (
                        <div key={i} className="border border-gray-600 transition-colors duration-300"></div>
                      ))}
                    </div>
                    <Monitor size={64} className="text-silver/20 group-hover:text-silver/40 transition-colors" />
                  </div>
                )}

                {/* Hover Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-300 ${hoveredApp === index ? 'opacity-100' : 'opacity-0'
                  }`}>
                  <div className="absolute bottom-6 left-6 right-6 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-silver text-black py-3 px-6 rounded-xl font-bold text-sm hover:bg-white transition-all duration-300 flex items-center justify-center gap-2 shadow-xl"
                      >
                        View Live Project
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* project Details */}
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-2xl font-bold text-silver group-hover:text-white transition-colors duration-300">
                    {project.title}
                  </h3>
                </div>

                <p className="text-gray-400 text-sm mb-6 leading-relaxed line-clamp-3">
                  {project.description}
                </p>

                {/* Technologies */}
                {project.tags && project.tags.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tech, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-white/5 text-silver text-xs font-mono rounded-full border border-white/10"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Accent Border */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-silver to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>

        {/* Portfolio CTA */}
        <div className="text-center">
          <div className="glass-morphism rounded-3xl p-10 md:p-16 max-w-4xl mx-auto border border-white/5 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-silver/20 to-transparent"></div>
            <h3 className="text-3xl md:text-5xl font-bold text-silver mb-6">
              Ready to Build Your <span className="gradient-text">Next Big Idea?</span>
            </h3>
            <p className="text-gray-400 mb-10 text-xl leading-relaxed">
              We specialize in turning complex requirements into streamlined enterprise solutions.
              Let's create something extraordinary together.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-silver text-black px-10 py-4 rounded-xl font-bold hover:bg-white transition-all duration-300 hover:scale-105 shadow-xl">
                Start Your Project
              </button>
              <button className="border border-silver/30 text-silver px-10 py-4 rounded-xl font-bold hover:bg-white/5 transition-all duration-300 hover:scale-105">
                Our Services
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
    </div>
  );
};

export default ShowcaseSection;
